import React, { useState, useMemo } from 'react';
import { getBranches } from '../api/mentorApi';
import {
  mergeSchoolBranches,
  resolveBranchLabel,
} from '../utils/schoolBranchOverrides';

// ── UI view-preference cache ─────────────────────────────────────────
// This is a READ-ONLY convenience cache for the PA's last branch pick.
// It is NEVER a source of business data — the roster/cohorts/sessions
// are always fetched live from the API. This respects CR-mentor-api-only-01
// (which removed localStorage mutation fallbacks for business data) because
// the branch selection is a UI preference, not business state.
const STORAGE_KEY = 'mentorSchoolBranchUIPref';

function loadSchoolBranchMap() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSchoolBranchMap(map) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // localStorage full or unavailable — degrade gracefully
  }
}

async function loadMergedBranchesForSchool(schoolId) {
  let apiBranches = [];
  try {
    apiBranches = await getBranches(schoolId);
  } catch {
    apiBranches = [];
  }
  return mergeSchoolBranches(schoolId, apiBranches || []);
}

export default function SchoolsList({ schools, students, schoolCohorts, onSelectSchool, branches = [], selectedBranchId, onSelectBranch }) {
  const [query, setQuery] = useState('');
  const [schoolBranchMap, setSchoolBranchMap] = useState(loadSchoolBranchMap);
  // pendingSchool: school waiting for branch assignment before navigation
  const [pendingSchool, setPendingSchool] = useState(null);
  const [pendingBranchId, setPendingBranchId] = useState('');
  // Branches scoped to the pending school (null = use global list)
  const [pendingSchoolBranches, setPendingSchoolBranches] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? schools.filter(s => (s.school_name || s.name || '').toLowerCase().includes(q))
      : schools;
    // Order by cohort count descending (max → min)
    const countFor = (s) =>
      (schoolCohorts || []).filter(c => String(c.schoolId) === String(s.id)).length;
    return [...list].sort((a, b) => countFor(b) - countFor(a));
  }, [schools, query, schoolCohorts]);

  const sortedBranches = useMemo(() => {
    return [...branches].sort((a, b) => (a.branch_name || '').localeCompare(b.branch_name || ''));
  }, [branches]);

  const openBranchModal = (school, schoolBranches, preselectId = '') => {
    setPendingSchool(school);
    setPendingBranchId(preselectId !== '' && preselectId != null ? String(preselectId) : '');
    setPendingSchoolBranches(schoolBranches);
  };

  const handleSchoolClick = async (school) => {
    const savedBranchId = schoolBranchMap[school.id] ?? schoolBranchMap[String(school.id)];

    // Always show the branch picker for every school (even a single campus).
    // Options = Central GET /branches?school_id=X plus any interim overrides (DPS).
    // Last pick is pre-selected when still valid; it never skips the modal.
    let schoolBranches = [];
    try {
      schoolBranches = await loadMergedBranchesForSchool(school.id);
    } catch {
      openBranchModal(school, null, savedBranchId || '');
      return;
    }

    if (schoolBranches.length === 0) {
      // No school-scoped campuses — fall back to global list in the modal if available.
      if (branches.length > 0) {
        openBranchModal(school, null, savedBranchId || '');
        return;
      }
      // No branches at all — cannot scope roster; open school without branch_id.
      onSelectSchool(school);
      return;
    }

    const pre =
      savedBranchId != null &&
      schoolBranches.some((b) => String(b.id) === String(savedBranchId))
        ? savedBranchId
        : '';
    openBranchModal(school, schoolBranches, pre);
  };

  const handleConfirmBranch = () => {
    if (!pendingSchool || !pendingBranchId) return;
    const branchId = Number(pendingBranchId);
    const options = pendingSchoolBranches || sortedBranches;
    const picked = (options || []).find((b) => String(b.id) === String(branchId));
    const branchName = picked?.branch_name || picked?.name || null;
    const updated = { ...schoolBranchMap, [pendingSchool.id]: branchId };
    setSchoolBranchMap(updated);
    saveSchoolBranchMap(updated);
    onSelectSchool({
      ...pendingSchool,
      branch_id: branchId,
      branch_name: branchName,
    });
    setPendingSchool(null);
    setPendingBranchId('');
    setPendingSchoolBranches(null);
  };

  /** Clear saved pick and re-open campus modal (does not navigate). */
  const handleChangeBranch = async (school, e) => {
    e.stopPropagation();
    const schoolId = school.id;
    const updated = { ...schoolBranchMap };
    delete updated[schoolId];
    delete updated[String(schoolId)];
    setSchoolBranchMap(updated);
    saveSchoolBranchMap(updated);

    const schoolBranches = await loadMergedBranchesForSchool(schoolId);
    if (schoolBranches.length === 0) {
      openBranchModal(school, null, '');
      return;
    }
    openBranchModal(school, schoolBranches, '');
  };

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--neutral-900)', margin: '0 0 4px 0' }}>Schools</h1>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
          {schools.length} schools · click a school to load its student roster
        </p>
      </div>

      {/* Branch assignment modal */}
      {pendingSchool && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'var(--surface)', borderRadius: '12px', padding: '28px 32px',
            width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: '700', color: 'var(--neutral-900)' }}>
              Select Branch for {pendingSchool.school_name || pendingSchool.name}
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: 'var(--text-muted)' }}>
              Choose the campus for this school. Even a single campus must be confirmed so the roster loads for the correct branch.
              Your last choice is remembered as the default next time.
            </p>
            <select
              value={pendingBranchId}
              onChange={e => setPendingBranchId(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                border: '1px solid var(--neutral-300)', borderRadius: '8px',
                background: 'var(--surface)', color: 'var(--neutral-900)',
                marginBottom: '20px'
              }}
            >
              <option value="">— Select a branch —</option>
              {(pendingSchoolBranches || sortedBranches).map(b => (
                <option key={b.id} value={b.id}>{b.branch_name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { setPendingSchool(null); setPendingBranchId(''); setPendingSchoolBranches(null); }}
                style={{
                  padding: '8px 18px', borderRadius: '8px', border: '1px solid var(--neutral-300)',
                  background: 'none', color: 'var(--neutral-700)', cursor: 'pointer', fontSize: '14px'
                }}
              >Cancel</button>
              <button
                type="button"
                onClick={handleConfirmBranch}
                disabled={!pendingBranchId}
                style={{
                  padding: '8px 18px', borderRadius: '8px', border: 'none',
                  background: 'var(--primary-navy)', color: '#fff', cursor: pendingBranchId ? 'pointer' : 'default',
                  fontSize: '14px', fontWeight: '600', opacity: pendingBranchId ? 1 : 0.5
                }}
              >Load Students</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by school name…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{
            width: '100%', maxWidth: '480px', padding: '10px 14px', fontSize: '14px',
            border: '1px solid var(--neutral-300)', borderRadius: '8px', outline: 'none',
            boxSizing: 'border-box', background: 'var(--surface)', color: 'var(--neutral-900)'
          }}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state-card" style={{ padding: '40px 24px' }}>
          <div className="empty-state-icon-wrapper">🏫</div>
          <h3 className="empty-state-heading">No schools found</h3>
          <p className="empty-state-description">Try a different search term.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {filtered.map(school => {
            const schoolId = school.id;
            const schoolName = school.school_name || school.name || '';
            const cohorts = schoolCohorts ? schoolCohorts.filter(c => String(c.schoolId) === String(schoolId)) : [];
            const cohortCount = cohorts.length;
            const assignedCount = cohorts.reduce((sum, c) => sum + (c.studentIds ? c.studentIds.length : 0), 0);
            const savedId = schoolBranchMap[schoolId] ?? schoolBranchMap[String(schoolId)];
            const savedBranchName = savedId != null
              ? resolveBranchLabel(schoolId, savedId, branches)
              : null;

            return (
              <div
                key={schoolId}
                onClick={() => handleSchoolClick(school)}
                style={{
                  background: 'var(--surface)', border: '1px solid var(--neutral-200)',
                  borderRadius: '12px', padding: '20px', cursor: 'pointer',
                  transition: 'box-shadow 0.15s, border-color 0.15s'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  e.currentTarget.style.borderColor = 'var(--primary-500, #6366f1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'var(--neutral-200)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: 'var(--neutral-900)' }}>{schoolName}</h3>
                    {savedBranchName ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--primary-navy)', fontWeight: 500 }}>
                          {savedBranchName}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleChangeBranch(school, e)}
                          title="Change branch"
                          style={{
                            fontSize: '11px', color: 'var(--primary-navy)', background: 'none',
                            border: 'none', cursor: 'pointer', padding: '0', lineHeight: 1,
                            textDecoration: 'underline', fontWeight: 400
                          }}
                        >Change</button>
                      </div>
                    ) : branches.length > 0 ? (
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>tap to assign branch</span>
                    ) : null}
                  </div>
                  <span style={{ fontSize: '20px' }}>🏫</span>
                </div>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ background: 'var(--neutral-100)', borderRadius: '6px', padding: '6px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--neutral-900)' }}>{cohortCount}</div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cohorts</div>
                  </div>
                  {assignedCount > 0 && (
                    <div style={{ background: 'var(--neutral-100)', borderRadius: '6px', padding: '6px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--neutral-900)' }}>{assignedCount}</div>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Assigned</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
